'use client';

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "overlay", "onHide", "onShow", "hideCloseButton", "withPortal", "placement", "domNode"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { cloneElement, isValidElement, useEffect } from 'react';
import { ENCORE_DATA_IDS } from "../../dataEncoreIds.js";
import { useTrigger } from "../../hooks/useTrigger.js";
import { deprecatedOverlayDirections, mapPhysicalToLogicalDirections } from "../../styles/mixins/index.js";
import { OverlayPortal } from "../OverlayPortal/index.js";
import { Overlay } from "../OverlayTrigger/Overlay.js";
import { physicalDirectionWarning } from "../../logging.js";
import styled from "../../styled-components.js";
import { isWindowDefined } from "../../utils.js";
var PopoverTriggerContainer = styled.div.withConfig({
  displayName: "PopoverTrigger__PopoverTriggerContainer",
  componentId: "sc-yux5vv-0"
})(["display:inline-flex;position:relative;"]);
var TriggerContainer = styled.div.withConfig({
  displayName: "PopoverTrigger__TriggerContainer",
  componentId: "sc-yux5vv-1"
})(["display:inline-flex;"]);
function PopoverTriggerComponent(_ref) {
  var children = _ref.children,
    overlay = _ref.overlay,
    _ref$onHide = _ref.onHide,
    onHide = _ref$onHide === void 0 ? function () {} : _ref$onHide,
    _ref$onShow = _ref.onShow,
    onShow = _ref$onShow === void 0 ? function () {} : _ref$onShow,
    hideCloseButton = _ref.hideCloseButton,
    withPortal = _ref.withPortal,
    placement = _ref.placement,
    domNode = _ref.domNode,
    props = _objectWithoutProperties(_ref, _excluded);
  if (placement) {
    physicalDirectionWarning(placement, 'PopoverTrigger');
  }
  var _useTrigger = useTrigger({
      overlay: overlay,
      onShow: onShow,
      onHide: onHide,
      withPortal: withPortal
    }),
    triggerRef = _useTrigger.triggerRef,
    overlayRef = _useTrigger.overlayRef,
    onClick = _useTrigger.onClick;
  /* if escape key is pressed, execute onHide */
  var onKeydown = function onKeydown(event) {
    if (event.keyCode === 27 || event.code === 'Escape') {
      onHide(event);
    }
  };
  useEffect(function () {
    /* add event listener for escape key press */
    window.addEventListener('keydown', onKeydown, true);

    /* clean up */
    return function () {
      window.removeEventListener('keydown', onKeydown, true);
    };
  });
  var clonedOverlay = null;
  if (overlay && typeof overlay === 'object' && /*#__PURE__*/isValidElement(overlay)) {
    clonedOverlay = /*#__PURE__*/cloneElement(overlay, {
      onClose: hideCloseButton ? undefined : onClick
    });
  }
  return /*#__PURE__*/React.createElement(PopoverTriggerContainer, Object.assign({
    ref: triggerRef
  }, props, {
    "data-encore-id": ENCORE_DATA_IDS.PopoverTrigger
  }), /*#__PURE__*/React.createElement(TriggerContainer, {
    onClick: onClick
  }, children), overlay && (withPortal && isWindowDefined() && typeof triggerRef !== 'function' && triggerRef.current ? /*#__PURE__*/React.createElement(OverlayPortal, {
    overlayPosition: triggerRef.current.getBoundingClientRect(),
    closeOverlay: onHide,
    domNode: domNode
  }, /*#__PURE__*/React.createElement(Overlay, {
    ref: overlayRef,
    $placement: mapPhysicalToLogicalDirections(placement)
  }, clonedOverlay)) : /*#__PURE__*/React.createElement(Overlay, {
    ref: overlayRef,
    $placement: mapPhysicalToLogicalDirections(placement)
  }, clonedOverlay)));
}

// export placement options
var popoverTriggerHelpers = _objectSpread({}, deprecatedOverlayDirections);

/**
 * The trigger that launches a Popover, upon user interaction.
 * Our Triggers are not viewport-aware and may render off screen in more dynamic situations.
 * Consider a library such as [Tippy.js](https://atomiks.github.io/tippyjs/) or [Floating UI](https://floating-ui.com/) if you need smarter positioning.
 *
 * [Storybook](https://encore-web.spotify.net/?path=/docs/components-popover-popovertrigger--docs) |
 * [Encore site](https://encore.spotify.net/web/components/popover) |
 * [Github](https://ghe.spotify.net/encore/web/tree/master/src/core/components/PopoverTrigger)
 */
export var PopoverTrigger = Object.assign(PopoverTriggerComponent, popoverTriggerHelpers);